<script setup lang="ts">
import { computed } from "vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";
import { ButtonSeverity, TuButton } from "@tinyurldev/tu-ui-vue";
import moment from "moment";

import useTranslation from "@Composables/useTranslation";

//
// Setup
const { __ } = useTranslation();
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Props
interface Props {
  backupCodesCreatedDate?: string | null;
  isGenerating?: boolean;
  isDeleting?: boolean;
  isFetching?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  backupCodesCreatedDate: null,
  isGenerating: false,
  isDeleting: false,
  isFetching: false,
});

//
// Emit
interface Events {
  (e: "generate"): void;
  (e: "delete"): void;
}

const emit = defineEmits<Events>();

//
// Computed
const lastGeneratedOnLabel = computed<string | null>(() => {
  if (!props.backupCodesCreatedDate) {
    return null;
  }

  return __("security_settings.backup_codes.last_generated_on", {
    date: moment(props.backupCodesCreatedDate).format("lll"),
  });
});

const generateLabel = computed<string>(() =>
  props.backupCodesCreatedDate
    ? __("security_settings.backup_codes.generate_new")
    : __("security_settings.backup_codes.generate"),
);
</script>

<template>
  <div
    class="two-factor-backup-codes"
    :class="{ 'two-factor-backup-codes--mobile': isMobileView }"
  >
    <div class="two-factor-backup-codes__copy">
      <p class="two-factor-backup-codes__description">
        {{ __("security_settings.backup_codes.description") }}
      </p>

      <p
        v-if="lastGeneratedOnLabel"
        class="two-factor-backup-codes__generated-date _generated_date"
      >
        {{ lastGeneratedOnLabel }}
      </p>
    </div>

    <div class="two-factor-backup-codes__actions">
      <TuButton
        class="_generate"
        :label="generateLabel"
        is-outlined
        :severity="ButtonSeverity.PRIMARY"
        :is-disabled="isGenerating || isFetching"
        :is-block="isMobileView"
        @click="emit('generate')"
      />
      <TuButton
        v-if="backupCodesCreatedDate"
        class="_delete_codes"
        :label="__('security_settings.backup_codes.delete')"
        is-outlined
        :severity="ButtonSeverity.ALERT"
        :is-disabled="isDeleting"
        :is-block="isMobileView"
        @click="emit('delete')"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;

.two-factor-backup-codes {
  $self: &;

  display: flex;
  flex-flow: column;
  align-items: flex-start;
  gap: spacing.$xxs;
  padding: spacing.$xxs;

  background-color: color.$background-secondary;
  border-radius: border.$medium-radius;
  border: border.$primary;

  &__copy {
    display: flex;
    flex-flow: column;
    gap: spacing.$xxs;
  }

  &__description,
  &__generated-date {
    margin: 0;

    font-weight: typography.$font-weight-medium;
  }

  &__description {
    font-size: typography.$base-font-size;
    line-height: typography.$base-line-height;
  }

  &__generated-date {
    font-size: typography.$xs-font-size;
    line-height: typography.$xs-line-height;
  }

  &__actions {
    display: flex;
    flex-flow: wrap;
    gap: spacing.$xxs;
  }

  &--mobile {
    #{ $self }__actions {
      flex-flow: column;
      align-self: stretch;
    }
  }
}
</style>
